package com.dotdrives.client

import com.dotdrives.model.FormPaymentReadiness
import com.dotdrives.core.uuidV7
import com.dotdrives.config.BuildKonfig
import com.dotdrives.model.FormsDashboardSnapshot
import com.dotdrives.model.FormLogoUpload
import com.dotdrives.model.validateFormLogo
import com.dotdrives.model.FormConfig
import com.dotdrives.model.FormOrgStats
import com.dotdrives.model.FormStats
import com.dotdrives.model.FormSubmission
import com.dotdrives.model.GivingForm
import com.dotdrives.model.GivingFormStatus
import com.dotdrives.model.NewGivingForm
import com.dotdrives.model.Target
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.auth.auth
import io.github.jan.supabase.postgrest.postgrest
import io.github.jan.supabase.postgrest.rpc
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Order
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.buildJsonObject
import kotlinx.serialization.json.put
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.encodeToJsonElement
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive
import io.ktor.client.HttpClient
import io.ktor.client.request.header
import io.ktor.client.request.post
import io.ktor.client.request.setBody
import io.ktor.client.statement.bodyAsText
import io.ktor.http.ContentType
import io.ktor.http.HttpHeaders
import io.ktor.http.contentType
import io.ktor.http.isSuccess
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant

class FormClient(
    private val client: SupabaseClient = SupabaseProvider.client,
    private val baseUrl: String = BuildKonfig.SUPABASE_URL,
    private val publicKey: String = BuildKonfig.SUPABASE_PUBLISHABLE_KEY,
    private val httpFactory: () -> HttpClient = { HttpClient() },
) {
    suspend fun uploadLogo(orgId: String, formId: String, name: String, mime: String, bytes: ByteArray): FormLogoUpload {
        validateFormLogo(name, mime, bytes)
        require(Regex("[0-9a-fA-F-]{36}").matches(orgId) && Regex("[0-9a-fA-F-]{36}").matches(formId)) { "Invalid form identity." }
        val extension = when (mime) { "image/png" -> "png"; "image/jpeg" -> "jpg"; else -> "webp" }
        val path = "$orgId/$formId/${uuidV7()}.$extension"
        val jwt = client.auth.currentSessionOrNull()?.accessToken ?: error("Sign in again to upload a logo.")
        val http = httpFactory()
        try {
            val response = http.post("${baseUrl.trimEnd('/')}/storage/v1/object/form-logos/$path") {
                header(HttpHeaders.Authorization, "Bearer $jwt")
                header("apikey", publicKey)
                contentType(ContentType.parse(mime))
                setBody(bytes)
            }
            if (!response.status.isSuccess()) error(if (response.status.value in listOf(401,403))
                "You do not have permission to upload a logo for this form."
                else "Logo upload failed. Try again.")
        } finally { http.close() }
        return FormLogoUpload("${baseUrl.trimEnd('/')}/storage/v1/object/public/form-logos/$path", name, mime, bytes.size)
    }

    /** One request; private logos and full configuration are loaded when the editor opens. */
    suspend fun dashboard(orgId: String): FormsDashboardSnapshot {
        val row = client.postgrest.rpc("forms_dashboard_snapshot", buildJsonObject {
            put("p_org", orgId)
        }).decodeAs<FormsDashboardRow>()
        return FormsDashboardSnapshot(
            forms = row.forms.map { it.toDomain(it.targetName) },
            stats = row.stats?.associate { it.formId to FormStats(it.formId, it.raisedAmount, it.giftsCount, it.recurringCount) },
            orgStats = row.orgStats?.let { FormOrgStats(it.raisedLast30Days, it.giftsLast30Days) },
            activePartnersCount = row.activePartnersCount,
            stripeConnected = row.stripeConnected,
            paymentReadiness = row.paymentReadiness,
            targets = row.targets,
        )
    }

    suspend fun targets(orgId: String): List<Target> =
        client.from(TARGETS_TABLE)
            .select {
                order("name", Order.ASCENDING)
                filter {
                    eq("org_id", orgId)
                    eq("is_active", true)
                }
            }
            .decodeList()

    suspend fun listForms(orgId: String): List<GivingForm> {
        val rows = client.from(FORMS_TABLE)
            .select {
                order("updated_at", Order.DESCENDING)
                filter { eq("org_id", orgId) }
            }
            .decodeList<GivingFormRow>()
        val targetNames = loadTargetNames(orgId, rows.mapNotNull(GivingFormRow::targetId).distinct())
        return rows.map { it.toDomain(targetNames[it.targetId]) }
    }

    suspend fun getForm(orgId: String, formId: String): GivingForm {
        val row = client.from(FORMS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", formId)
                }
            }
            .decodeSingle<GivingFormRow>()
        val targetName = row.targetId?.let { loadTargetName(orgId, it) }
        return row.toDomain(targetName, resolveLogo(orgId, row.config))
    }

    suspend fun createForm(orgId: String, form: NewGivingForm): GivingForm {
        val id = uuidV7()
        val inserted = client.from(FORMS_TABLE).insert(
            GivingFormInsert(
                id = id,
                orgId = orgId,
                targetId = form.targetId,
                name = form.name,
                publicSlug = form.publicSlug,
                headline = form.headline,
                type = form.type,
                config = encodeConfig(form.config.forStorage()),
            ),
        ) {
            select()
        }.decodeSingle<GivingFormRow>()
        val targetName = inserted.targetId?.let { loadTargetName(orgId, it) }
        return inserted.toDomain(targetName, resolveLogo(orgId, inserted.config))
    }

    /**
     * `config` is one JSONB document -- `version` is bumped here on every save that touches it
     * (docs/schema.md). Reads the row's current version first so the bump is relative to what's
     * actually stored, not to a possibly-stale value the caller loaded earlier. `target_id` is a
     * real column, not part of `config`, but the builder's Design tab edits it in the same save
     * as amounts/toggles, so it is written in the same request rather than a second round trip.
     */
    suspend fun saveConfig(orgId: String, formId: String, config: FormConfig, targetId: String?): GivingForm {
        val current = client.from(FORMS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    eq("id", formId)
                }
            }
            .decodeSingle<VersionRow>()
        val updated = client.from(FORMS_TABLE).update(
            FormConfigUpdate(config = encodeConfig(config.forStorage()), version = current.version + 1, targetId = targetId),
        ) {
            select()
            filter {
                eq("org_id", orgId)
                eq("id", formId)
            }
        }.decodeSingle<GivingFormRow>()
        val targetName = updated.targetId?.let { loadTargetName(orgId, it) }
        return updated.toDomain(targetName, resolveLogo(orgId, updated.config))
    }

    suspend fun saveSettings(
        orgId: String,
        formId: String,
        name: String,
        headline: String,
        goalAmount: Double?,
        showProgressBar: Boolean,
        closeAt: Instant?,
    ): GivingForm {
        val updated = client.from(FORMS_TABLE).update(
            FormSettingsUpdate(
                name = name,
                headline = headline,
                goalAmount = goalAmount,
                showProgressBar = showProgressBar,
                closeAt = closeAt,
            ),
        ) {
            select()
            filter {
                eq("org_id", orgId)
                eq("id", formId)
            }
        }.decodeSingle<GivingFormRow>()
        val targetName = updated.targetId?.let { loadTargetName(orgId, it) }
        return updated.toDomain(targetName, resolveLogo(orgId, updated.config))
    }

    suspend fun setStatus(orgId: String, formId: String, status: String): GivingForm {
        val updated = client.from(FORMS_TABLE).update(
            FormStatusUpdate(status = status),
        ) {
            select()
            filter {
                eq("org_id", orgId)
                eq("id", formId)
            }
        }.decodeSingle<GivingFormRow>()
        val targetName = updated.targetId?.let { loadTargetName(orgId, it) }
        return updated.toDomain(targetName, resolveLogo(orgId, updated.config))
    }

    /**
     * The builder's "Submission feed" -- restricted to `status = 'completed'` (a pending or
     * failed attempt is not a gift and must not appear in a LIVE feed or its total) and the last
     * 30 days, matching the design's "This form · 30 days" footer. Returns null when no rows
     * are visible, so the caller cannot render
     * "No gifts yet" to a role whose rows might just be hidden -- see [com.dotdrives.repository.FormRepository.submissionFeed].
     */
    suspend fun submissionFeed(orgId: String, formId: String, limit: Int): List<FormSubmission>? {
        val cutoff = Clock.System.now().minus(THIRTY_DAYS)
        val rows = client.from(SUBMISSIONS_TABLE)
            .select {
                order("created_at", Order.DESCENDING)
                this.limit(limit.toLong())
                filter {
                    eq("org_id", orgId)
                    eq("form_id", formId)
                    eq("status", COMPLETED_STATUS)
                    gte("created_at", cutoff.toString())
                }
            }
            .decodeList<FormSubmissionRow>()
        if (rows.isEmpty()) return null
        return rows.map {
            FormSubmission(
                id = it.id,
                formId = it.formId,
                formVersion = it.formVersion,
                donorName = it.donorName,
                amount = it.amount,
                isRecurring = it.isRecurring,
                status = it.status,
                createdAt = it.createdAt,
            )
        }
    }

    suspend fun paymentReadiness(orgId: String): FormPaymentReadiness =
        client.postgrest.rpc("form_payment_readiness", buildJsonObject { put("p_org", orgId) })
            .decodeAs<FormPaymentReadiness>()

    suspend fun stripeConnected(orgId: String): Boolean = paymentReadiness(orgId).connectionPreserved

    private suspend fun loadTargetNames(orgId: String, targetIds: List<String>): Map<String, String> {
        if (targetIds.isEmpty()) return emptyMap()
        return client.from(TARGETS_TABLE)
            .select {
                filter {
                    eq("org_id", orgId)
                    isIn("id", targetIds)
                }
            }
            .decodeList<FormTargetNameRow>()
            .associate { it.id to it.name }
    }

    private suspend fun loadTargetName(orgId: String, targetId: String): String? =
        loadTargetNames(orgId, listOf(targetId))[targetId]

    private suspend fun resolveLogo(orgId: String, config: FormConfig): FormConfig {
        val path = config.design.logoStoragePath ?: return config
        val prefix = "$orgId/form-design/"
        if (!path.startsWith(prefix) || path.contains("..")) {
            return config.copy(design = config.design.copy(logoUrl = null))
        }
        val signedUrl = runCatching {
            val jwt = client.auth.currentSessionOrNull()?.accessToken ?: return@runCatching null
            val http = httpFactory()
            try {
                val response = http.post("${baseUrl.trimEnd('/')}/storage/v1/object/sign/org-private/$path") {
                    header(HttpHeaders.Authorization, "Bearer $jwt")
                    header("apikey", publicKey)
                    contentType(ContentType.Application.Json)
                    setBody("""{"expiresIn":300}""")
                }
                if (!response.status.isSuccess()) return@runCatching null
                val relative = Json.parseToJsonElement(response.bodyAsText()).jsonObject["signedURL"]?.jsonPrimitive?.content
                    ?: return@runCatching null
                if (!relative.startsWith("/object/sign/org-private/$prefix")) return@runCatching null
                "${baseUrl.trimEnd('/')}/storage/v1$relative"
            } finally {
                http.close()
            }
        }.getOrNull()
        return config.copy(design = config.design.copy(logoUrl = signedUrl))
    }
}

private fun GivingFormRow.toDomain(targetName: String?, resolvedConfig: FormConfig = config): GivingForm =
    GivingForm(
        id = id,
        orgId = orgId,
        targetId = targetId,
        targetName = targetName,
        name = name,
        publicSlug = publicSlug,
        headline = headline,
        type = type,
        status = status,
        goalAmount = goalAmount,
        showProgressBar = showProgressBar,
        closeAt = closeAt,
        config = resolvedConfig,
        version = version,
        createdAt = createdAt,
        updatedAt = updatedAt,
    )

@Serializable
private data class GivingFormRow(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("target_id")
    val targetId: String?,
    val name: String,
    @SerialName("public_slug")
    val publicSlug: String,
    val headline: String,
    val type: String,
    val status: String,
    @SerialName("goal_amount")
    val goalAmount: Double?,
    @SerialName("show_progress_bar")
    val showProgressBar: Boolean,
    @SerialName("close_at")
    val closeAt: Instant?,
    val config: FormConfig = FormConfig(),
    @SerialName("target_name") val targetName: String? = null,
    val version: Int,
    @SerialName("created_at")
    val createdAt: Instant,
    @SerialName("updated_at")
    val updatedAt: Instant,
)

@Serializable
private data class GivingFormInsert(
    val id: String,
    @SerialName("org_id")
    val orgId: String,
    @SerialName("target_id")
    val targetId: String?,
    val name: String,
    @SerialName("public_slug")
    val publicSlug: String,
    val headline: String,
    val type: String,
    val config: JsonElement,
    val status: String = GivingFormStatus.DRAFT,
)

@Serializable
private data class VersionRow(val version: Int)

@Serializable
private data class FormConfigUpdate(
    val config: JsonElement,
    val version: Int,
    @SerialName("target_id")
    val targetId: String?,
)

@Serializable
private data class FormSettingsUpdate(
    val name: String,
    val headline: String,
    @SerialName("goal_amount")
    val goalAmount: Double?,
    @SerialName("show_progress_bar")
    val showProgressBar: Boolean,
    @SerialName("close_at")
    val closeAt: Instant?,
)

@Serializable
private data class FormStatusUpdate(val status: String)

@Serializable
private data class FormSubmissionRow(
    val id: String,
    @SerialName("form_id")
    val formId: String,
    @SerialName("form_version")
    val formVersion: Int,
    @SerialName("donor_name")
    val donorName: String,
    val amount: Double,
    @SerialName("is_recurring")
    val isRecurring: Boolean,
    val status: String,
    @SerialName("created_at")
    val createdAt: Instant,
)



@Serializable
private data class FormTargetNameRow(
    val id: String,
    val name: String,
)

/**
 * `giving_forms.config` is one JSONB document read by this builder and by Task 15's public-form
 * Edge Function -- a genuinely cross-task wire contract (docs/schema.md's documented shape).
 * kotlinx.serialization's `Json` defaults to `encodeDefaults = false`, which silently OMITS any
 * property whose value equals its declared Kotlin default -- so a form left at the stock toggle
 * defaults (`FormToggles()`) would encode with no `toggles` key at all, and a stock receipt with
 * `include_irs_language = true` would drop that key too. Encoding through this instance instead
 * of relying on Postgrest's ambient serializer keeps the written document's shape independent of
 * which fields happen to match a Kotlin default -- see GivingFormSerializationTest, the only
 * test in this module that actually encodes/decodes `FormConfig` rather than comparing Kotlin
 * objects to each other.
 */
// internal (not private): GivingFormSerializationTest asserts through this exact instance, not
// a lookalike Json{} it builds itself -- otherwise deleting encodeDefaults=true here would leave
// every test green while the real write path resumed dropping default-valued keys.
internal val configJson = Json { encodeDefaults = true }

private fun encodeConfig(config: FormConfig): JsonElement = configJson.encodeToJsonElement(config)

/** Display-only signed URLs are never written back over the stable private-storage key. */
private fun FormConfig.forStorage(): FormConfig =
    if (design.logoStoragePath == null) this else copy(design = design.copy(logoUrl = null))

private const val FORMS_TABLE = "giving_forms"
private const val SUBMISSIONS_TABLE = "form_submissions"
private const val TARGETS_TABLE = "targets"
private const val COMPLETED_STATUS = "completed"
private val THIRTY_DAYS = 30.days

@Serializable
private data class FormsDashboardRow(
    val forms: List<GivingFormRow>,
    val stats: List<FormDashboardStatsRow>?,
    @SerialName("org_stats") val orgStats: FormDashboardOrgStatsRow?,
    @SerialName("active_partners_count") val activePartnersCount: Int?,
    @SerialName("stripe_connected") val stripeConnected: Boolean,
    @SerialName("payment_readiness") val paymentReadiness: FormPaymentReadiness? = null,
    val targets: List<Target>,
)

@Serializable
private data class FormDashboardStatsRow(
    @SerialName("form_id") val formId: String,
    @SerialName("raised_amount") val raisedAmount: Double,
    @SerialName("gifts_count") val giftsCount: Int,
    @SerialName("recurring_count") val recurringCount: Int,
)

@Serializable
private data class FormDashboardOrgStatsRow(
    @SerialName("raised_last_30_days") val raisedLast30Days: Double,
    @SerialName("gifts_last_30_days") val giftsLast30Days: Int,
)
